import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import {
    Box, Container, Typography, Button, CircularProgress, Paper,
    FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Chip, IconButton, Tooltip,
    ToggleButton, ToggleButtonGroup, Snackbar, Alert, TablePagination,
    InputAdornment,
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CommentIcon from '@mui/icons-material/Comment';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import CakeIcon from '@mui/icons-material/Cake';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import { useTranslation } from 'react-i18next';
import { elUpperCase } from '../utils/text';

import { NAV_BG, AMBER, AMBER_DARK, AMBER_GLOW } from '../theme';
const GREEN = '#10b981';
const ORANGE = '#f97316';

function toISODate(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function shiftDate(isoDate, days) {
    const d = new Date(isoDate + 'T00:00:00');
    d.setDate(d.getDate() + days);
    return toISODate(d);
}

const AttendancePage = () => {
    const { authTokens, logoutUser, backendUrl, pageTitle } = useContext(AuthContext);
    const { t } = useTranslation();
    const navigate = useNavigate();
    document.title = (pageTitle || 'App') + ' - ' + t('daily_register.title');

    const today = new Date();
    const [date, setDate] = useState(toISODate(today));
    const isFuture = date > toISODate(today);
    const [classes, setClasses] = useState([]);
    const [selectedClassId, setSelectedClassId] = useState('');
    const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
    const [attendance, setAttendance] = useState([]);
    const [loadingClasses, setLoadingClasses] = useState(true);
    const [loadingAttendance, setLoadingAttendance] = useState(false);

    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const [commentDialogOpen, setCommentDialogOpen] = useState(false);
    const [commentChild, setCommentChild] = useState(null);
    const [commentType, setCommentType] = useState(0);
    const [commentNotes, setCommentNotes] = useState('');
    const [commentTime, setCommentTime] = useState('');
    const [commentSaving, setCommentSaving] = useState(false);

    const [dailyLogs, setDailyLogs] = useState([]);
    const [logDialogOpen, setLogDialogOpen] = useState(false);
    const [logDialogChild, setLogDialogChild] = useState(null);
    const [logForm, setLogForm] = useState({ breakfast: 0, lunch: 0, afternoon: 0, slept: false, comments: '' });
    const [logSaving, setLogSaving] = useState(false);

    const [studentDialogOpen, setStudentDialogOpen] = useState(false);
    const [studentDialogChild, setStudentDialogChild] = useState(null);

    const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });
    const [birthdayChildIds, setBirthdayChildIds] = useState(new Set());

    const authHeader = useCallback(() => ({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + String(authTokens.access),
    }), [authTokens]);

    useEffect(() => {
        if (!date) return;
        fetch(backendUrl + `api/reports/birthdays/?date=${date}`, { headers: authHeader() })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (!data) return;
                setBirthdayChildIds(new Set(data.children.map(c => c.id)));
            })
            .catch(() => setToast({ open: true, msg: t('common.network_error'), severity: 'error' }));
    }, [date, authHeader, backendUrl]);

    useEffect(() => {
        fetch(backendUrl + 'api/get-classes/', { headers: authHeader() })
            .then(r => { if (r.status === 401) { logoutUser(); return null; } return r.json(); })
            .then(data => { if (!data) return; setClasses(data); setLoadingClasses(false); })
            .catch(() => setToast({ open: true, msg: t('common.network_error'), severity: 'error' }));
    }, []);

    const allStudents = useMemo(() => {
        if (selectedClassId) {
            const sc = classes.find(c => String(c.id) === selectedClassId);
            if (!sc) return [];
            return sc.departments.flatMap(d =>
                d.children.map(child => ({ ...child, department_id: String(d.id), department_name: d.name, class_name: sc.name }))
            );
        }
        return classes.flatMap(sc =>
            sc.departments.flatMap(d =>
                d.children.map(child => ({ ...child, department_id: String(d.id), department_name: d.name, class_name: sc.name }))
            )
        );
    }, [selectedClassId, classes]);

    const availableDepartments = useMemo(() => {
        const sourceClasses = selectedClassId
            ? classes.filter(c => String(c.id) === selectedClassId)
            : classes;
        return sourceClasses.flatMap(c => c.departments.map(d => ({ id: d.id, name: d.name })));
    }, [selectedClassId, classes]);

    const filteredStudents = useMemo(() => {
        let students = allStudents;
        if (selectedDepartmentId)
            students = students.filter(s => String(s.department_id) === selectedDepartmentId);
        if (searchText.trim()) {
            const lower = searchText.toLowerCase();
            students = students.filter(s => `${s.firstname} ${s.lastname}`.toLowerCase().includes(lower));
        }
        return students;
    }, [allStudents, selectedDepartmentId, searchText]);

    const paginatedStudents = filteredStudents.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

    useEffect(() => { setPage(0); }, [searchText, selectedClassId, selectedDepartmentId]);
    useEffect(() => { setSelectedDepartmentId(''); }, [selectedClassId]);

    const loadAttendance = useCallback(async () => {
        if (!date) return;
        setLoadingAttendance(true);
        try {
            const params = new URLSearchParams({ date });
            if (selectedClassId) params.set('schoolclass_id', selectedClassId);
            const [attRes, logRes] = await Promise.all([
                fetch(backendUrl + `api/attendance/?${params}`, { headers: authHeader() }),
                fetch(backendUrl + `api/daily-log/?${params}`, { headers: authHeader() }),
            ]);
            if (attRes.status === 401) { logoutUser(); return; }
            if (attRes.ok) setAttendance(await attRes.json());
            if (logRes.ok) setDailyLogs(await logRes.json());
        } catch {
            setToast({ open: true, msg: t('common.network_error'), severity: 'error' });
        } finally {
            setLoadingAttendance(false);
        }
    }, [date, selectedClassId, authHeader, backendUrl, logoutUser]);

    useEffect(() => { loadAttendance(); }, [loadAttendance]);

    const attendanceByChild = {};
    attendance.forEach(a => {
        if (!attendanceByChild[a.child_id]) attendanceByChild[a.child_id] = [];
        attendanceByChild[a.child_id].push(a);
    });

    const dailyLogsByChild = {};
    dailyLogs.forEach(l => { dailyLogsByChild[l.child_id] = l; });

    const openStudentDialog = (child) => {
        setStudentDialogChild(child);
        setStudentDialogOpen(true);
    };

    const openLogDialog = (child) => {
        setLogDialogChild(child);
        const existing = dailyLogsByChild[child.id];
        setLogForm(existing
            ? { breakfast: existing.breakfast, lunch: existing.lunch, afternoon: existing.afternoon, slept: existing.slept, comments: existing.comments }
            : { breakfast: 0, lunch: 0, afternoon: 0, slept: false, comments: '' }
        );
        setLogDialogOpen(true);
    };

    const handleLogSave = async () => {
        if (!logDialogChild) return;
        setLogSaving(true);
        try {
            const r = await fetch(backendUrl + 'api/daily-log/save/', {
                method: 'POST',
                headers: authHeader(),
                body: JSON.stringify({ child_id: logDialogChild.id, date, ...logForm }),
            });
            if (r.ok) {
                const saved = await r.json();
                setDailyLogs(prev => {
                    const rest = prev.filter(l => l.child_id !== logDialogChild.id);
                    return [...rest, saved];
                });
                setToast({ open: true, msg: t('daily_log.saved', { name: logDialogChild.firstname }), severity: 'success' });
            }
        } finally {
            setLogSaving(false);
            setLogDialogOpen(false);
        }
    };

    const handleQuickLog = async (child, type) => {
        const now = new Date();
        const ts = new Date(date);
        ts.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), 0);
        const r = await fetch(backendUrl + 'api/attendance/log/', {
            method: 'POST',
            headers: authHeader(),
            body: JSON.stringify({ child_id: child.id, attend_type: type, timestamp: ts.toISOString(), notes: '' }),
        });
        if (r.ok) {
            const msgKey = type === 0 ? 'attendance.arrival_logged' : type === 1 ? 'attendance.departure_logged' : 'attendance.absent_logged';
            setToast({ open: true, msg: t(msgKey, { name: child.firstname }), severity: type === 2 ? 'warning' : 'success' });
            loadAttendance();
        }
    };

    const openCommentDialog = (child, type) => {
        if (isFuture) return;
        setCommentChild(child);
        setCommentType(type);
        setCommentNotes('');
        const now = new Date();
        setCommentTime(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
        setCommentDialogOpen(true);
    };

    const handleCommentSave = async () => {
        if (!commentChild) return;
        setCommentSaving(true);
        const [h, m] = commentTime.split(':').map(Number);
        const ts = new Date(date);
        ts.setHours(h || 0, m || 0, 0, 0);
        try {
            const r = await fetch(backendUrl + 'api/attendance/log/', {
                method: 'POST',
                headers: authHeader(),
                body: JSON.stringify({ child_id: commentChild.id, attend_type: commentType, timestamp: ts.toISOString(), notes: commentNotes }),
            });
            if (r.ok) {
                setToast({ open: true, msg: t(commentType === 0 ? 'attendance.arrival_logged' : 'attendance.departure_logged', { name: commentChild.firstname }), severity: 'success' });
                loadAttendance();
            }
        } finally {
            setCommentSaving(false);
            setCommentDialogOpen(false);
        }
    };

    const handleDelete = async (attendanceId) => {
        await fetch(backendUrl + `api/attendance/${attendanceId}/`, { method: 'DELETE', headers: authHeader() });
        loadAttendance();
    };

    if (loadingClasses) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
            <CircularProgress size={44} thickness={3} sx={{ color: AMBER }} />
        </Box>
    );

    const arrived = attendance.filter(a => a.attend_type === 0).length;
    const departed = attendance.filter(a => a.attend_type === 1).length;

    return (
        <Box sx={{ minHeight: '100vh', background: '#f1f5f9', pt: 4, pb: 6 }}>
            <Container maxWidth="xl">

                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 42, height: 42, borderRadius: '12px', background: `linear-gradient(135deg, ${NAV_BG} 0%, #163571 100%)`, boxShadow: '0 4px 14px rgba(15,45,94,0.25)', flexShrink: 0 }}>
                            <EventAvailableIcon sx={{ color: AMBER, fontSize: 22 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.5rem', color: NAV_BG, lineHeight: 1.2 }}>{t('daily_register.title')}</Typography>
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#94a3b8', mt: 0.1 }}>{t('daily_register.subtitle')}</Typography>
                        </Box>
                    </Box>

                    {/* Stats */}
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Chip icon={<LoginIcon sx={{ fontSize: 14 }} />} label={`${arrived} ${t('attendance.arrived_stat')}`} size="small"
                            sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, background: 'rgba(16,185,129,0.12)', color: GREEN, fontSize: '0.8rem' }} />
                        <Chip icon={<LogoutIcon sx={{ fontSize: 14 }} />} label={`${departed} ${t('attendance.departed_stat')}`} size="small"
                            sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, background: 'rgba(249,115,22,0.12)', color: ORANGE, fontSize: '0.8rem' }} />
                    </Box>
                </Box>

                {/* Controls */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={t('attendance.prev_day')} arrow>
                            <IconButton size="small" onClick={() => setDate(d => shiftDate(d, -1))}
                                sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', p: 0.5, color: '#64748b', '&:hover': { borderColor: AMBER, color: AMBER } }}>
                                <ChevronLeftIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                        <TextField type="date" size="small" label={t('attendance.date')} value={date} onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 160, '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: AMBER }, '& .MuiInputLabel-root.Mui-focused': { color: AMBER }, '& .MuiInputBase-input': { fontFamily: '"Outfit", sans-serif' } }} />
                        <Tooltip title={t('attendance.next_day')} arrow>
                            <IconButton size="small" onClick={() => setDate(d => shiftDate(d, 1))}
                                sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', p: 0.5, color: '#64748b', '&:hover': { borderColor: AMBER, color: AMBER } }}>
                                <ChevronRightIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem' }}>{t('attendance.class')}</InputLabel>
                        <Select value={selectedClassId} label={t('attendance.class')} onChange={(e) => setSelectedClassId(e.target.value)}
                            sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', borderRadius: '10px', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: AMBER } }}>
                            <MenuItem value="" sx={{ fontFamily: '"Outfit", sans-serif', fontStyle: 'italic', color: '#94a3b8' }}>{t('attendance.all_classes')}</MenuItem>
                            {classes.map(sc => (
                                <MenuItem key={sc.id} value={String(sc.id)} sx={{ fontFamily: '"Outfit", sans-serif' }}>{sc.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem' }}>{t('attendance.department')}</InputLabel>
                        <Select value={selectedDepartmentId} label={t('attendance.department')} onChange={(e) => setSelectedDepartmentId(e.target.value)}
                            sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', borderRadius: '10px', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: AMBER } }}>
                            <MenuItem value="" sx={{ fontFamily: '"Outfit", sans-serif', fontStyle: 'italic', color: '#94a3b8' }}>{t('attendance.all_departments')}</MenuItem>
                            {availableDepartments.map(d => (
                                <MenuItem key={d.id} value={String(d.id)} sx={{ fontFamily: '"Outfit", sans-serif' }}>{d.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField size="small" placeholder={t('attendance.search_students')} value={searchText} onChange={(e) => setSearchText(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
                        sx={{ minWidth: 220, '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: AMBER }, '& .MuiInputBase-input': { fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem' } }} />
                    {loadingAttendance && <CircularProgress size={20} thickness={3} sx={{ color: AMBER }} />}
                </Box>

                {isFuture && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, mb: 2, borderRadius: '12px', background: 'rgba(100,116,139,0.08)', border: '1px solid #e2e8f0' }}>
                        <LockOutlinedIcon sx={{ fontSize: 18, color: '#94a3b8', flexShrink: 0 }} />
                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', color: '#64748b' }}>
                            {t('attendance.future_locked')}
                        </Typography>
                    </Box>
                )}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 380px' }, gap: 3 }}>

                    {/* Students list */}
                    <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 24px rgba(15,45,94,0.07)' }}>
                        <Box sx={{ px: 3, py: 2, background: `linear-gradient(180deg, ${NAV_BG} 0%, #163571 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.9rem', color: 'rgba(241,245,249,0.9)', letterSpacing: '0.04em' }}>
                                {elUpperCase(t('attendance.students_header', { count: filteredStudents.length }))}
                            </Typography>
                        </Box>
                        {filteredStudents.length === 0 ? (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <DirectionsRunIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#94a3b8', fontSize: '0.9rem' }}>
                                    {searchText ? t('attendance.no_match') : t('attendance.no_students')}
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {paginatedStudents.map((student) => {
                                    const records = attendanceByChild[student.id] || [];
                                    const arrivalRecord = records.filter(r => r.attend_type === 0).at(-1);
                                    const departureRecord = records.filter(r => r.attend_type === 1).at(-1);
                                    const absenceRecord = records.filter(r => r.attend_type === 2).at(-1);
                                    const existingLog = dailyLogsByChild[student.id];
                                    const PURPLE = '#8b5cf6';
                                    const RED = '#ef4444';
                                    return (
                                        <Box key={student.id} sx={{ display: 'flex', alignItems: 'center', px: 2.5, py: 1.5, borderBottom: '1px solid #f1f5f9', '&:last-child': { borderBottom: 'none' }, '&:hover': { background: 'rgba(15,45,94,0.02)' }, opacity: absenceRecord ? 0.65 : 1 }}>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Typography onClick={() => openStudentDialog(student)}
                                                        sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.9rem', color: NAV_BG, cursor: 'pointer', '&:hover': { color: AMBER, textDecoration: 'underline' } }}>
                                                        {student.firstname} {student.lastname}
                                                    </Typography>
                                                    {birthdayChildIds.has(student.id) && (
                                                        <Tooltip title={t('reports.tab_birthdays')} arrow>
                                                            <CakeIcon sx={{ fontSize: 16, color: '#F59E0B' }} />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.25 }}>
                                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', color: '#94a3b8' }}>
                                                        {[student.class_name, student.department_name].filter(Boolean).join(' · ')}
                                                    </Typography>
                                                    {existingLog && !absenceRecord && (
                                                        <>
                                                            {existingLog.breakfast > 0 && <Chip label={`🍳 ×${existingLog.breakfast}`} size="small" sx={{ height: 18, fontSize: '0.68rem', fontFamily: '"Outfit", sans-serif', background: 'rgba(139,92,246,0.1)', color: PURPLE }} />}
                                                            {existingLog.lunch > 0 && <Chip label={`🍽 ×${existingLog.lunch}`} size="small" sx={{ height: 18, fontSize: '0.68rem', fontFamily: '"Outfit", sans-serif', background: 'rgba(139,92,246,0.1)', color: PURPLE }} />}
                                                            {existingLog.afternoon > 0 && <Chip label={`🍪 ×${existingLog.afternoon}`} size="small" sx={{ height: 18, fontSize: '0.68rem', fontFamily: '"Outfit", sans-serif', background: 'rgba(139,92,246,0.1)', color: PURPLE }} />}
                                                            {existingLog.slept && <Chip icon={<BedtimeIcon sx={{ fontSize: 11 }} />} label={t('daily_log.slept')} size="small" sx={{ height: 18, fontSize: '0.68rem', fontFamily: '"Outfit", sans-serif', background: 'rgba(139,92,246,0.1)', color: PURPLE }} />}
                                                        </>
                                                    )}
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                {absenceRecord ? (
                                                    <Tooltip title={t('attendance.undo_absent')} arrow>
                                                        <Chip icon={<PersonOffIcon sx={{ fontSize: 12 }} />} label={t('attendance.chip_absent')} size="small"
                                                            onDelete={!isFuture ? () => handleDelete(absenceRecord.id) : undefined}
                                                            sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', fontWeight: 600, background: 'rgba(239,68,68,0.1)', color: RED, height: 24, '& .MuiChip-deleteIcon': { fontSize: 14, color: RED, opacity: 0.6, '&:hover': { opacity: 1 } } }} />
                                                    </Tooltip>
                                                ) : (
                                                    <>
                                                        {arrivalRecord ? (
                                                            <Tooltip title={t('attendance.undo_arrival')} arrow>
                                                                <Chip icon={<CheckCircleIcon sx={{ fontSize: 13 }} />} label={t('attendance.chip_arrived')} size="small"
                                                                    onDelete={!isFuture ? () => handleDelete(arrivalRecord.id) : undefined}
                                                                    sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', fontWeight: 600, background: 'rgba(16,185,129,0.12)', color: GREEN, height: 24, '& .MuiChip-deleteIcon': { fontSize: 14, color: GREEN, opacity: 0.6, '&:hover': { opacity: 1, color: '#ef4444' } } }} />
                                                            </Tooltip>
                                                        ) : null}
                                                        {departureRecord && (
                                                            <Tooltip title={t('attendance.undo_departure')} arrow>
                                                                <Chip icon={<CheckCircleIcon sx={{ fontSize: 13 }} />} label={t('attendance.chip_departed')} size="small"
                                                                    onDelete={!isFuture ? () => handleDelete(departureRecord.id) : undefined}
                                                                    sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', fontWeight: 600, background: 'rgba(249,115,22,0.12)', color: ORANGE, height: 24, '& .MuiChip-deleteIcon': { fontSize: 14, color: ORANGE, opacity: 0.6, '&:hover': { opacity: 1, color: '#ef4444' } } }} />
                                                            </Tooltip>
                                                        )}
                                                    </>
                                                )}

                                                {!isFuture && !absenceRecord && (
                                                    <>
                                                        <Tooltip title={t('attendance.log_arrival')} arrow>
                                                            <IconButton size="small" onClick={() => handleQuickLog(student, 0)}
                                                                sx={{ color: GREEN, border: '1px solid rgba(16,185,129,0.3)', borderRadius: '6px', p: 0.5, '&:hover': { background: 'rgba(16,185,129,0.08)' } }}>
                                                                <LoginIcon sx={{ fontSize: 15 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={t('attendance.log_arrival_note')} arrow>
                                                            <IconButton size="small" onClick={() => openCommentDialog(student, 0)}
                                                                sx={{ color: '#94a3b8', borderRadius: '6px', p: 0.5, '&:hover': { color: GREEN, background: 'rgba(16,185,129,0.08)' } }}>
                                                                <CommentIcon sx={{ fontSize: 13 }} />
                                                            </IconButton>
                                                        </Tooltip>

                                                        <Tooltip title={t('attendance.log_departure')} arrow>
                                                            <IconButton size="small" onClick={() => handleQuickLog(student, 1)}
                                                                sx={{ color: ORANGE, border: '1px solid rgba(249,115,22,0.3)', borderRadius: '6px', p: 0.5, '&:hover': { background: 'rgba(249,115,22,0.08)' } }}>
                                                                <LogoutIcon sx={{ fontSize: 15 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={t('attendance.log_departure_note')} arrow>
                                                            <IconButton size="small" onClick={() => openCommentDialog(student, 1)}
                                                                sx={{ color: '#94a3b8', borderRadius: '6px', p: 0.5, '&:hover': { color: ORANGE, background: 'rgba(249,115,22,0.08)' } }}>
                                                                <CommentIcon sx={{ fontSize: 13 }} />
                                                            </IconButton>
                                                        </Tooltip>

                                                        <Tooltip title={t('attendance.log_absent')} arrow>
                                                            <IconButton size="small" onClick={() => handleQuickLog(student, 2)}
                                                                sx={{ color: RED, border: `1px solid rgba(239,68,68,0.3)`, borderRadius: '6px', p: 0.5, '&:hover': { background: 'rgba(239,68,68,0.08)' } }}>
                                                                <PersonOffIcon sx={{ fontSize: 15 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}

                                                {!isFuture && (
                                                    <Tooltip title={t('daily_log.open_log')} arrow>
                                                        <IconButton size="small" onClick={() => openLogDialog(student)}
                                                            sx={{ color: existingLog ? '#8b5cf6' : '#94a3b8', border: existingLog ? '1px solid rgba(139,92,246,0.4)' : '1px solid transparent', borderRadius: '6px', p: 0.5, '&:hover': { color: '#8b5cf6', background: 'rgba(139,92,246,0.08)' } }}>
                                                            <AssignmentIcon sx={{ fontSize: 15 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}
                                <TablePagination
                                    component="div"
                                    count={filteredStudents.length}
                                    page={page}
                                    onPageChange={(_, newPage) => setPage(newPage)}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                    rowsPerPageOptions={[10, 25, 50]}
                                    sx={{ borderTop: '1px solid #f1f5f9', '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': { fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem' } }}
                                />
                            </>
                        )}
                    </Paper>

                    {/* Attendance log */}
                    <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 24px rgba(15,45,94,0.07)', height: 'fit-content' }}>
                        <Box sx={{ px: 3, py: 2, background: `linear-gradient(180deg, ${NAV_BG} 0%, #163571 100%)` }}>
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.9rem', color: 'rgba(241,245,249,0.9)', letterSpacing: '0.04em' }}>
                                {elUpperCase(t('attendance.log_for', { date }))}
                            </Typography>
                        </Box>
                        {attendance.length === 0 ? (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#94a3b8', fontSize: '0.875rem' }}>{t('attendance.no_records')}</Typography>
                            </Box>
                        ) : (
                            attendance.map((a) => (
                                <Box key={a.id} sx={{ display: 'flex', alignItems: 'flex-start', px: 2.5, py: 1.5, gap: 1.5, borderBottom: '1px solid #f1f5f9', '&:last-child': { borderBottom: 'none' } }}>
                                    <Box sx={{ mt: 0.25, flexShrink: 0 }}>
                                        {a.attend_type === 0
                                            ? <LoginIcon sx={{ fontSize: 16, color: GREEN }} />
                                            : <LogoutIcon sx={{ fontSize: 16, color: ORANGE }} />}
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.85rem', color: NAV_BG }}>
                                            {a.child_name}
                                        </Typography>
                                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', color: '#64748b' }}>
                                            {a.attend_type === 0 ? t('attendance.chip_arrived') : t('attendance.chip_departed')} · {new Date(a.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            {a.logged_by && ` · ${a.logged_by}`}
                                        </Typography>
                                        {a.notes && (
                                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic', mt: 0.25 }}>
                                                {a.notes}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Tooltip title={t('attendance.remove')} arrow>
                                        <IconButton size="small" onClick={() => handleDelete(a.id)}
                                            sx={{ color: '#cbd5e1', p: 0.3, '&:hover': { color: '#f87171', background: 'rgba(248,113,113,0.08)' } }}>
                                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            ))
                        )}
                    </Paper>
                </Box>

                {/* Comment / notes dialog */}
                <Dialog open={commentDialogOpen} onClose={() => setCommentDialogOpen(false)} maxWidth="xs" fullWidth
                    PaperProps={{ sx: { borderRadius: '16px', p: 1 }, onKeyDown: (e) => { if (e.key === 'Enter' && !e.shiftKey && !commentSaving) handleCommentSave(); } }}>
                    <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, pb: 1 }}>
                        {t(commentType === 0 ? 'attendance.arrival' : 'attendance.departure')} — {commentChild && `${commentChild.firstname} ${commentChild.lastname}`}
                    </DialogTitle>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '12px !important' }}>
                        <ToggleButtonGroup value={commentType} exclusive onChange={(_, v) => { if (v !== null) setCommentType(v); }} size="small" sx={{ alignSelf: 'flex-start' }}>
                            <ToggleButton value={0} sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', px: 2, '&.Mui-selected': { background: 'rgba(16,185,129,0.12)', color: GREEN } }}>
                                <LoginIcon sx={{ fontSize: 15, mr: 0.75 }} /> {t('attendance.arrival')}
                            </ToggleButton>
                            <ToggleButton value={1} sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', px: 2, '&.Mui-selected': { background: 'rgba(249,115,22,0.12)', color: ORANGE } }}>
                                <LogoutIcon sx={{ fontSize: 15, mr: 0.75 }} /> {t('attendance.departure')}
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <TextField label={t('attendance.time')} type="time" size="small" value={commentTime} onChange={(e) => setCommentTime(e.target.value)} InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: AMBER }, '& .MuiInputLabel-root.Mui-focused': { color: AMBER }, '& .MuiInputBase-input': { fontFamily: '"Outfit", sans-serif' } }} />
                        <TextField label={t('attendance.notes')} size="small" multiline rows={3} autoFocus value={commentNotes} onChange={(e) => setCommentNotes(e.target.value)}
                            placeholder={t('attendance.notes_placeholder')}
                            sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: AMBER }, '& .MuiInputLabel-root.Mui-focused': { color: AMBER }, '& .MuiInputBase-input': { fontFamily: '"Outfit", sans-serif' } }} />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                        <Button onClick={() => setCommentDialogOpen(false)} sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', color: '#64748b' }}>{t('attendance.cancel')}</Button>
                        <Button variant="contained" onClick={handleCommentSave} disabled={commentSaving}
                            startIcon={commentSaving ? <CircularProgress size={14} color="inherit" /> : null}
                            sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600, borderRadius: '8px', background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`, color: NAV_BG, boxShadow: `0 4px 14px ${AMBER_GLOW}`, '&:hover': { background: `linear-gradient(135deg, #fbbf24 0%, ${AMBER} 100%)` } }}>
                            {commentSaving ? t('attendance.saving') : t('attendance.save')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Daily log dialog */}
                <Dialog open={logDialogOpen} onClose={() => setLogDialogOpen(false)} maxWidth="xs" fullWidth
                    PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                    <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, pb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AssignmentIcon sx={{ color: '#8b5cf6', fontSize: 20 }} />
                            {t('daily_log.title')} — {logDialogChild && `${logDialogChild.firstname} ${logDialogChild.lastname}`}
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '12px !important' }}>
                        {[
                            { key: 'breakfast', label: t('daily_log.breakfast'), emoji: '🍳' },
                            { key: 'lunch', label: t('daily_log.lunch'), emoji: '🍽' },
                            { key: 'afternoon', label: t('daily_log.afternoon'), emoji: '🍪' },
                        ].map(({ key, label, emoji }) => (
                            <Box key={key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#475569' }}>
                                    {emoji} {label}
                                </Typography>
                                <ToggleButtonGroup value={logForm[key]} exclusive size="small"
                                    onChange={(_, v) => { if (v !== null) setLogForm(f => ({ ...f, [key]: v })); }}>
                                    {[0, 1, 2].map(v => (
                                        <ToggleButton key={v} value={v} sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', px: 1.5, '&.Mui-selected': { background: 'rgba(139,92,246,0.12)', color: '#8b5cf6' } }}>
                                            {v === 0 ? '—' : `×${v}`}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Box>
                        ))}

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#475569' }}>
                                <BedtimeIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle', color: '#8b5cf6' }} />
                                {t('daily_log.slept')}
                            </Typography>
                            <ToggleButtonGroup value={logForm.slept ? 'yes' : 'no'} exclusive size="small"
                                onChange={(_, v) => { if (v !== null) setLogForm(f => ({ ...f, slept: v === 'yes' })); }}>
                                <ToggleButton value="yes" sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', px: 1.5, '&.Mui-selected': { background: 'rgba(139,92,246,0.12)', color: '#8b5cf6' } }}>{t('common.yes')}</ToggleButton>
                                <ToggleButton value="no" sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', px: 1.5, '&.Mui-selected': { background: 'rgba(100,116,139,0.1)', color: '#64748b' } }}>{t('common.no')}</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        <TextField label={t('daily_log.comments')} size="small" multiline rows={3}
                            value={logForm.comments} onChange={(e) => setLogForm(f => ({ ...f, comments: e.target.value }))}
                            placeholder={t('daily_log.comments_placeholder')}
                            sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#8b5cf6' }, '& .MuiInputLabel-root.Mui-focused': { color: '#8b5cf6' }, '& .MuiInputBase-input': { fontFamily: '"Outfit", sans-serif' } }} />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                        <Button onClick={() => setLogDialogOpen(false)} sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', color: '#64748b' }}>{t('attendance.cancel')}</Button>
                        <Button variant="contained" onClick={handleLogSave} disabled={logSaving}
                            startIcon={logSaving ? <CircularProgress size={14} color="inherit" /> : null}
                            sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600, borderRadius: '8px', background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', color: '#fff', '&:hover': { background: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)' } }}>
                            {logSaving ? t('attendance.saving') : t('attendance.save')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Student details dialog */}
                <Dialog open={studentDialogOpen} onClose={() => setStudentDialogOpen(false)} maxWidth="xs" fullWidth
                    PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                    <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, pb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {studentDialogChild && `${studentDialogChild.firstname} ${studentDialogChild.lastname}`}
                            {studentDialogChild && birthdayChildIds.has(studentDialogChild.id) && (
                                <Tooltip title={t('reports.tab_birthdays')} arrow>
                                    <CakeIcon sx={{ fontSize: 18, color: '#F59E0B' }} />
                                </Tooltip>
                            )}
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                        {studentDialogChild && (() => {
                            const records = attendanceByChild[studentDialogChild.id] || [];
                            const arrivalRecord = records.filter(r => r.attend_type === 0).at(-1);
                            const departureRecord = records.filter(r => r.attend_type === 1).at(-1);
                            const absenceRecord = records.filter(r => r.attend_type === 2).at(-1);
                            const existingLog = dailyLogsByChild[studentDialogChild.id];
                            const PURPLE = '#8b5cf6';
                            return (
                                <>
                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#94a3b8', mt: -1 }}>
                                        {[studentDialogChild.class_name, studentDialogChild.department_name].filter(Boolean).join(' · ')}
                                    </Typography>

                                    <Box>
                                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.75rem', color: '#94a3b8', letterSpacing: '0.04em', mb: 1 }}>
                                            {elUpperCase(t('attendance.log_for', { date }))}
                                        </Typography>
                                        {absenceRecord ? (
                                            <Chip icon={<PersonOffIcon sx={{ fontSize: 14 }} />} label={t('attendance.chip_absent')} size="small"
                                                sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, background: 'rgba(239,68,68,0.1)', color: '#ef4444' }} />
                                        ) : records.length === 0 ? (
                                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#94a3b8' }}>{t('attendance.no_records')}</Typography>
                                        ) : (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {arrivalRecord && (
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                        <LoginIcon sx={{ fontSize: 16, color: GREEN, mt: 0.25 }} />
                                                        <Box>
                                                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', fontWeight: 600, color: NAV_BG }}>
                                                                {t('attendance.chip_arrived')} · {new Date(arrivalRecord.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                            </Typography>
                                                            {arrivalRecord.notes && (
                                                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }}>{arrivalRecord.notes}</Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                )}
                                                {departureRecord && (
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                        <LogoutIcon sx={{ fontSize: 16, color: ORANGE, mt: 0.25 }} />
                                                        <Box>
                                                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', fontWeight: 600, color: NAV_BG }}>
                                                                {t('attendance.chip_departed')} · {new Date(departureRecord.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                            </Typography>
                                                            {departureRecord.notes && (
                                                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }}>{departureRecord.notes}</Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Box>

                                    <Box>
                                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.75rem', color: '#94a3b8', letterSpacing: '0.04em', mb: 1 }}>
                                            {elUpperCase(t('daily_log.title'))}
                                        </Typography>
                                        {existingLog ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                                    {existingLog.breakfast > 0 && <Chip label={`🍳 ${t('daily_log.breakfast')} ×${existingLog.breakfast}`} size="small" sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', background: 'rgba(139,92,246,0.1)', color: PURPLE }} />}
                                                    {existingLog.lunch > 0 && <Chip label={`🍽 ${t('daily_log.lunch')} ×${existingLog.lunch}`} size="small" sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', background: 'rgba(139,92,246,0.1)', color: PURPLE }} />}
                                                    {existingLog.afternoon > 0 && <Chip label={`🍪 ${t('daily_log.afternoon')} ×${existingLog.afternoon}`} size="small" sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', background: 'rgba(139,92,246,0.1)', color: PURPLE }} />}
                                                    {existingLog.slept && <Chip icon={<BedtimeIcon sx={{ fontSize: 12 }} />} label={t('daily_log.slept')} size="small" sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', background: 'rgba(139,92,246,0.1)', color: PURPLE }} />}
                                                </Box>
                                                {existingLog.comments && (
                                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#64748b', fontStyle: 'italic' }}>{existingLog.comments}</Typography>
                                                )}
                                            </Box>
                                        ) : (
                                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#94a3b8' }}>{t('attendance.no_log')}</Typography>
                                        )}
                                    </Box>
                                </>
                            );
                        })()}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                        <Button onClick={() => setStudentDialogOpen(false)} sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', color: '#64748b' }}>{t('common.close')}</Button>
                        <Button variant="contained" startIcon={<LaunchIcon sx={{ fontSize: 16 }} />}
                            onClick={() => navigate(`/student/${studentDialogChild.id}`)}
                            sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600, borderRadius: '8px', background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`, color: NAV_BG, boxShadow: `0 4px 14px ${AMBER_GLOW}`, '&:hover': { background: `linear-gradient(135deg, #fbbf24 0%, ${AMBER} 100%)` } }}>
                            {t('attendance.view_profile')}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar open={toast.open} autoHideDuration={20000} onClose={() => setToast(t => ({ ...t, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))} sx={{ fontFamily: '"Outfit", sans-serif' }}>{toast.msg}</Alert>
                </Snackbar>

            </Container>
        </Box>
    );
};

export default AttendancePage;
